import { IconButton } from '@atlaskit/button/new';
import EditIcon from '@atlaskit/icon/core/edit';
import { type PopupProps, type TriggerProps } from '@atlaskit/popup';
import { Box, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import Popup from '@databases/atlaskit/Popup';
import { messages } from '@databases/intl/messages';
import { useComponent, useStateWithOverride } from '@databases/react';
import { type DefaultRootState, defaultStoreUtils } from '@databases/redux';
import { type SelectedValue } from '@databases/redux/cellSelections/slice';
import { DatabaseFieldType, type DatabaseValue } from '@databases/types/database';
import { getDatabaseFieldOptional } from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import DatabaseValueBulkEdit from '@databases/value/DatabaseValueBulkEdit';
import { isEqual } from 'lodash-es';
import React, {
  type DetailedHTMLProps,
  type FunctionComponent,
  type HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { useIntl } from 'react-intl-next';
import styled from 'styled-components';

const ContentContainer = styled.div`
  padding: ${token('space.050', '4px')};
`;

const pickerContainerStyles = xcss({
  display: 'flex',
  flexDirection: 'column',
  minWidth: '200px'
});

const richTextPopoverContainerStyles = xcss({
  position: 'absolute',
  bottom: '100%',
  marginBlockEnd: 'space.100',
  backgroundColor: 'elevation.surface.overlay',
  borderRadius: 'radius.200',
  boxShadow: 'elevation.shadow.overlay',
  padding: 'space.050',
  zIndex: 'modal'
});

const richTextPopoverWrapperStyles = xcss({
  position: 'relative',
  display: 'inline-block'
});

type TriggerPropsWithHtmlProps = TriggerProps &
  DetailedHTMLProps<HTMLAttributes<HTMLDivElement>, HTMLDivElement>;

export type BulkEditValuesProps = {
  contentId: string;
  initialValue?: DatabaseValue;
  isOpen?: boolean;
  offset?: [number, number];
  placement?: PopupProps['placement'];
  removeSelectedContent: () => void;
  selectedValues: SelectedValue[];
  Trigger?: (props: TriggerPropsWithHtmlProps) => React.ReactElement;
};

const BulkEditValues: FunctionComponent<BulkEditValuesProps> = ({
  contentId,
  initialValue,
  isOpen,
  offset,
  placement,
  removeSelectedContent,
  selectedValues,
  Trigger
}) => {
  const [value, setValue] = useState<DatabaseValue | undefined>(initialValue);
  const prevValue = useRef(value);
  const resetBulkEditValue = useCallback(() => {
    prevValue.current = undefined;
    setValue(undefined);
  }, []);

  const handleBulkEditChange = useCallback(
    (value: DatabaseValue) => {
      if (prevValue.current === undefined && initialValue === undefined) {
        removeSelectedContent();
      }

      prevValue.current = value;

      setValue(value);
    },
    [initialValue, removeSelectedContent]
  );

  const selectedFieldId = selectedValues[0]?.fieldId;

  const isRichTextField = defaultStoreUtils.useEqualSelector(
    (state: DefaultRootState) => {
      const database = selectDatabaseByContentIdRequired(state, { contentId });
      const field = getDatabaseFieldOptional(database, selectedFieldId);

      return field?.type === DatabaseFieldType.TEXT;
    }
  );

  const [isBulkEditOpen, setIsBulkEditOpen] = useStateWithOverride(false, isOpen);
  const handleToggleIsOpen = useCallback(() => {
    if (!isBulkEditOpen) {
      resetBulkEditValue();
    }
    setIsBulkEditOpen(!isBulkEditOpen);
  }, [isBulkEditOpen, resetBulkEditValue, setIsBulkEditOpen]);

  const wasBulkEditOpen = useRef(isBulkEditOpen);

  useEffect(() => {
    if (isBulkEditOpen && !wasBulkEditOpen.current) {
      resetBulkEditValue();
    }

    wasBulkEditOpen.current = isBulkEditOpen;
  }, [isBulkEditOpen, resetBulkEditValue]);

  const prevSelectedValues = useRef(selectedValues);

  useEffect(() => {
    if (isBulkEditOpen && !isEqual(selectedValues, prevSelectedValues.current)) {
      resetBulkEditValue();
    }

    prevSelectedValues.current = selectedValues;
  }, [isBulkEditOpen, resetBulkEditValue, selectedValues]);

  const selectedEntryIds = useMemo(
    () => selectedValues.map((v) => v.entryId),
    [selectedValues]
  );

  const { formatMessage } = useIntl();
  const trigger: PopupProps['trigger'] = useComponent(
    ({ ref, ...props }: TriggerProps) => {
      return Trigger && typeof trigger === 'function' ? (
        Trigger({
          ref,
          ...props
        })
      ) : (
        <Tooltip content={formatMessage(messages['bulk-edit-values.tooltip'])}>
          <IconButton
            icon={(iconProps) => <EditIcon {...iconProps} size="medium" />}
            label={formatMessage(messages['bulk-edit-values.label'])}
            spacing="compact"
            appearance="subtle"
            isSelected={isBulkEditOpen}
            onClick={(e: React.MouseEvent) => {
              handleToggleIsOpen();
              e.stopPropagation();
              e.preventDefault();
            }}
            ref={ref}
            {...props}
          />
        </Tooltip>
      );
    },
    [Trigger, formatMessage, handleToggleIsOpen, isBulkEditOpen]
  );

  if (isRichTextField) {
    const TriggerComponent = trigger;

    return (
      <Box xcss={richTextPopoverWrapperStyles}>
        <TriggerComponent {...({} as TriggerProps)} />
        {isBulkEditOpen && (
          <Box
            xcss={richTextPopoverContainerStyles}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          >
            <DatabaseValueBulkEdit
              contentId={contentId}
              value={value ?? initialValue}
              onBulkEditChange={handleBulkEditChange}
              selectedFieldId={selectedFieldId}
              selectedEntryIds={selectedEntryIds}
            />
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Popup
      id="bulk-edit-values"
      autoFocus={false}
      isOpen={isBulkEditOpen}
      onClose={handleToggleIsOpen}
      contentContainer={ContentContainer}
      trigger={trigger}
      placement={placement}
      offset={offset}
      content={({ setInitialFocusRef }) => (
        <Box
          xcss={pickerContainerStyles}
          onClick={(event: React.MouseEvent) => event.stopPropagation()}
        >
          <DatabaseValueBulkEdit
            contentId={contentId}
            value={value ?? initialValue}
            onBulkEditChange={handleBulkEditChange}
            selectedFieldId={selectedFieldId}
            selectedEntryIds={selectedEntryIds}
            ref={setInitialFocusRef}
          />
        </Box>
      )}
    />
  );
};

export default BulkEditValues;
